import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ArrowRight } from 'lucide-react';
import logo from '/logo_lightbg.svg';
import { forgotPasswordSchema, type ForgotPasswordData } from '@/lib/validations/auth';
import { useRequestPasswordReset } from '@/hooks/usePasswordReset';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const { requestReset, isLoading } = useRequestPasswordReset();

  const form = useForm<ForgotPasswordData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = (data: ForgotPasswordData) => {
    requestReset(data.email, {
      onSuccess: () => setSubmitted(true),
    });
  };

  return (
    <div className="flex flex-col items-center mt-[148px]">
      <img src={logo} alt="logo" className="h-[60px] mb-8" />
      <Card className="w-[480px] h-fit">
        <CardHeader className="px-8 pt-8">
          <CardTitle className="text-gray-900">Forgot your password?</CardTitle>
          <CardDescription className="text-gray-700 text-base pt-2">
            Enter the email on your Glitch account and a password reset link will be sent.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8 space-y-4">
          {submitted ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-l-4 border-l-[#290BD9] bg-[#EDE9FE] p-4 text-sm text-gray-900">
                If an account exists for that email, a reset link has been sent. The link is valid for 1 hour.
              </div>
              <Link
                to="/signin"
                className="block text-center text-sm text-[#290BD9] hover:text-[#2309C5] underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="you@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  size="lg"
                  disabled={isLoading}
                  className="w-full py-6 bg-[#290BD9] hover:bg-[#2309C5] uppercase"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <div className="text-center">
                  <Link
                    to="/signin"
                    className="text-sm text-[#290BD9] hover:text-[#2309C5] underline"
                  >
                    Back to sign in
                  </Link>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
